import React, { useState } from 'react'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
} from '@mui/material'
import {
  CalendarMonth,
  AccessTime,
  Percent,
  Search as SearchIcon,
} from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import { useLocation, Link as RouterLink } from 'react-router-dom'

import BookNowDialog from '../components/BookNowDialog'
import { calculatePrice } from '../utils/pricing'

const Bookings = ({ companyData }) => {
  const location = useLocation()
  const { groupBookings, company, jobTypes } = companyData || {}

  // Check if we have search results from navigation
  const { searchResults, searchPostcode, searchJobType, searchJobTypeId } =
    location.state || {}

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)

  // Get today's date for filtering
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day for accurate comparison

  // Use search results if available, otherwise show all active bookings
  const bookingsToDisplay = searchResults
    ? searchResults.bookings
    : groupBookings?.filter(b => {
        const bookingDate = new Date(b.date)
        return !b.completed && bookingDate >= today
      }) || []

  const activeBookings = bookingsToDisplay

  // Get the selected job type for price display
  const selectedJobType = searchJobTypeId
    ? jobTypes?.find(jt => jt.id === searchJobTypeId)
    : jobTypes?.[0]

  const formatTime = timeStr => {
    if (!timeStr) return 'N/A'
    const parts = timeStr.split(':')
    return `${parts[0]}:${parts[1]}`
  }

  const formatDate = dateStr => {
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy')
    } catch {
      return dateStr
    }
  }

  const handleBookClick = booking => {
    setSelectedBooking(booking)
    setDialogOpen(true)
  }

  return (
    <Container maxWidth='lg' sx={{ py: { xs: 6, md: 8 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant='h3' gutterBottom color='primary.main' sx={{ mb: 2 }}>
          Group Booking
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ fontSize: '1.1rem' }}>
          Join now to reduce the price.{selectedJobType?.minimumBookingPrice
            ? ` If the group booking is full, you'll pay just £${calculatePrice(selectedJobType.minimumBookingPrice, company).toFixed(2)}${company?.chargeVat ? ' inc VAT' : ''}.`
            : ''}
        </Typography>
      </Box>

      {/* Search Results Info */}
      {searchResults && (
        <Alert
          severity={searchResults.count > 0 ? 'success' : 'info'}
          sx={{ mb: 4 }}
          action={
            <Button
              component={RouterLink}
              to='/'
              size='small'
              startIcon={<SearchIcon />}
              sx={{ textTransform: 'none' }}
            >
              Search Again
            </Button>
          }
        >
          {searchResults.count > 0
            ? `Found ${searchResults.count} group booking${
                searchResults.count === 1 ? '' : 's'
              } available for ${searchJobType || 'your service'} in your area`
            : `No group bookings found for ${
                searchJobType || 'this service'
              } in your area. Try a different postcode or job type.`}
        </Alert>
      )}

      {activeBookings.length > 0 ? (
        <Grid container spacing={4}>
          {activeBookings.map(booking => (
            <Grid item xs={12} md={6} key={booking.id}>
              <Card
                sx={{
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography variant='h5' gutterBottom color='primary'>
                        {formatDate(booking.date)}
                      </Typography>
                      <Chip
                        label='Accepting Bookings'
                        sx={{
                          bgcolor: '#48eca2',
                          color: '#201e31',
                          fontWeight: 600,
                        }}
                        size='small'
                      />
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <AccessTime sx={{ color: 'text.secondary' }} />
                        <Box>
                          <Typography variant='body2' color='text.secondary'>
                            Time Window
                          </Typography>
                          <Typography variant='body1'>
                            {formatTime(booking.startTime)} -{' '}
                            {formatTime(booking.endTime)}
                          </Typography>
                        </Box>
                      </Box>

                      {booking.discountAmount &&
                        booking.discountCustomerInterval && (
                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Percent sx={{ color: 'secondary.main' }} />
                            <Box>
                              <Typography variant='body2' color='text.secondary'>
                                Group Discount
                              </Typography>
                              <Typography variant='body1'>
                                £{Number(booking.discountAmount).toFixed(2)} off
                                every {booking.discountCustomerInterval}
                                {booking.discountCustomerInterval === 1
                                  ? 'st'
                                  : booking.discountCustomerInterval === 2
                                  ? 'nd'
                                  : booking.discountCustomerInterval === 3
                                  ? 'rd'
                                  : 'th'}{' '}
                                customer
                              </Typography>
                            </Box>
                          </Box>
                        )}

                      {selectedJobType && booking.currentPrices?.[selectedJobType.id] != null && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <Typography
                            variant='h6'
                            sx={{ color: 'text.secondary', fontWeight: 'bold' }}
                          >
                            £
                          </Typography>
                          <Box>
                            <Typography variant='body1' sx={{ fontWeight: 600 }}>
                              £{booking.currentPrices[selectedJobType.id].toFixed(2)}{company?.chargeVat ? ' inc VAT' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {booking.message && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: 'secondary.light',
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant='body2'
                            sx={{ fontStyle: 'italic' }}
                          >
                            "{booking.message}"
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>

                  {/* Limited slots warning */}
                  {booking.showLimitedSlots && (
                    <Alert severity='warning' sx={{ mt: 2 }}>
                      <strong>Limited slots available!</strong> Book soon to secure
                      your spot.
                    </Alert>
                  )}

                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                      variant='contained'
                      startIcon={<CalendarMonth />}
                      onClick={() => handleBookClick(booking)}
                      sx={{
                        bgcolor: '#48eca2',
                        color: '#201e31',
                        '&:hover': { bgcolor: '#36d88f' },
                      }}
                    >
                      Book
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CalendarMonth sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant='h6' color='text.secondary' gutterBottom>
            No active group bookings at the moment
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Check back soon for new group booking opportunities!
          </Typography>
        </Box>
      )}

      {/* Request a date section */}
      {company?.email && (
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant='h6' color='text.secondary' gutterBottom>
            Haven't found a date that suits you?
          </Typography>
          <Button
            variant='outlined'
            href={`mailto:${company.email}?subject=Request a date`}
            sx={{ textTransform: 'none' }}
          >
            Request date
          </Button>
        </Box>
      )}

      <Box
        sx={{
          mt: 8,
          p: 4,
          bgcolor: 'primary.light',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Typography variant='h5' gutterBottom sx={{ mb: 2 }}>
          How Group Bookings Work
        </Typography>
        <Typography variant='body1'>
          Our smart tech groups you with nearby neighbours, cutting travel
          time for our engineers and passes the savings straight back to you.
          The more bookings on the day, the less you pay.
        </Typography>
      </Box>

      <BookNowDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        booking={selectedBooking}
        company={company}
        jobTypes={jobTypes}
        searchJobTypeId={searchJobTypeId}
        searchPostcode={searchPostcode}
      />
    </Container>
  )
}

export default Bookings
